import { useId, type ReactElement, type ReactNode } from 'react';

import { CountBadge } from '@/components/admin/CountBadge';

/**
 * WP風 メタボックス（指示書 §7-2）。
 *
 * - 中央にメインフォーム
 * - 「公開」ボックス（ステータス・公開ボタン・更新日時。イベント詳細はタブ上の横並び / D-350）
 * - 下に関連情報のカード（タブ切替。イベント詳細は9タブ、会員詳細は8タブ）
 *
 * 詳細/編集画面はこの部品を使い回すこと。
 */

export function MetaBoxLayout({
  main,
  side,
  below,
}: {
  main: ReactNode;
  side?: ReactNode;
  below?: ReactNode;
}): ReactElement {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-5 lg:flex-row">
        <div className="min-w-0 flex-1">{main}</div>
        {side && <div className="w-full shrink-0 lg:w-72">{side}</div>}
      </div>
      {below}
    </div>
  );
}

export function MetaBox({
  title,
  children,
  footer,
  badge,
  badgeLabel = '未対応',
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** 見出し横の件数。0 または未指定なら出さない（D-240） */
  badge?: number;
  badgeLabel?: string;
}): ReactElement {
  return (
    <section className="rounded-sm border border-wpborder bg-white">
      <h2 className="flex items-center gap-1.5 border-b border-wpborder px-5 py-3 text-wppanel font-semibold text-wptext-strong">
        {title}
        <CountBadge count={badge ?? 0} label={badgeLabel} />
      </h2>
      <div className="px-5 py-5">{children}</div>
      {footer && <div className="border-t border-wpborder px-5 py-3.5">{footer}</div>}
    </section>
  );
}

/** 「公開」ボックス。詳細画面のステータス表示・公開／状態変更。
 *
 * - `stack`（既定）: 縦積み（会員詳細などの右サイド向け）
 * - `bar`: 横並び1行（イベント詳細はタブが多く右サイドだと幅を食うため / D-350）
 */
export function PublishBox({
  statusLabel,
  updatedAt,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  extra,
  layout = 'stack',
}: {
  statusLabel: string;
  updatedAt?: string | null | undefined;
  // undefined = 操作できるロールが誰もいない（この画面の権限を持たない）。
  // その場合はボタン自体を出さない。「押せないボタン」は利用者を混乱させるため
  // 操作不能な状態をdisabledではなく非表示で表す
  primaryLabel?: string | undefined;
  onPrimary?: (() => void) | undefined;
  primaryDisabled?: boolean | undefined;
  extra?: ReactNode | undefined;
  layout?: 'stack' | 'bar';
}): ReactElement {
  if (layout === 'bar') {
    return (
      <section className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-sm border border-wpborder bg-white px-5 py-3">
        <h2 className="text-wppanel font-semibold text-wptext-strong">公開</h2>
        <p className="text-wptext-muted">
          ステータス{' '}
          <span className="font-semibold text-wptext-strong">{statusLabel}</span>
        </p>
        {updatedAt && (
          <p className="text-wptext-muted">
            更新日時 <span className="text-wptext">{updatedAt}</span>
          </p>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {extra}
          {primaryLabel && (
            <button
              type="button"
              onClick={onPrimary}
              disabled={primaryDisabled || !onPrimary}
              className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              {primaryLabel}
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <MetaBox title="公開">
      <dl className="mb-3 grid grid-cols-[5rem_1fr] gap-y-1">
        <dt className="text-wptext-muted">ステータス</dt>
        <dd className="font-semibold text-wptext-strong">{statusLabel}</dd>
        {updatedAt && (
          <>
            <dt className="text-wptext-muted">更新日時</dt>
            <dd>{updatedAt}</dd>
          </>
        )}
      </dl>
      {primaryLabel && (
        <button
          type="button"
          onClick={onPrimary}
          disabled={primaryDisabled || !onPrimary}
          className="w-full rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          {primaryLabel}
        </button>
      )}
      {/* ステータス変更などの副次操作は主要操作（編集・公開する等）と混同されないよう、
          区切り線とラベルで明確に分ける（会員詳細で「編集」が在籍状態の変更操作と
          誤解されたフィードバックによる） */}
      {extra && <div className={primaryLabel ? 'mt-4 border-t border-wpborder pt-3' : undefined}>{extra}</div>}
    </MetaBox>
  );
}

export interface MetaTab {
  id: string;
  label: string;
  content: ReactNode;
  /** タブ横の未対応件数（0 または未指定なら出さない / D-232） */
  badge?: number | undefined;
}

/** タブを複数行に分けるときの1行分（イベント詳細の課題・アンケート行 / D-360） */
export interface MetaTabRowDef {
  /** 行頭の補助見出し（省略可） */
  heading?: string;
  ids: string[];
}

/** 詳細画面のタブ切替カード（イベント詳細など）。
 *
 * 親が URL（`#tabId` / `?tab=`）と同期する前提で制御コンポーネントとして使う（D-354）。
 * クリック時は親が history に載せるので、ブラウザの戻るで直前のタブに戻れる。
 *
 * `tabRows` を渡すと行ごとに改行される（`flex-wrap` だけだと変な位置で折れるのを防ぐ）。
 */
export function MetaTabs({
  tabs,
  tabRows,
  activeTabId,
  onTabChange,
}: {
  tabs: MetaTab[];
  /** 省略時は全タブを1行。指定時は行ごとに tablist を分ける */
  tabRows?: MetaTabRowDef[];
  /** いま開いているタブ。未指定・不正なら先頭タブ */
  activeTabId?: string | undefined;
  onTabChange: (tabId: string) => void;
}): ReactElement {
  const fallback = tabs[0]?.id ?? '';
  const active =
    activeTabId && tabs.some((tab) => tab.id === activeTabId) ? activeTabId : fallback;
  const baseId = useId();
  const activeTab = tabs.find((tab) => tab.id === active) ?? tabs[0];

  const rows: { heading?: string; tabs: MetaTab[] }[] = tabRows
    ? tabRows
        .map((row) => {
          const mapped: { heading?: string; tabs: MetaTab[] } = {
            tabs: row.ids
              .map((id) => tabs.find((tab) => tab.id === id))
              .filter((tab): tab is MetaTab => tab !== undefined),
          };
          if (row.heading !== undefined) {
            mapped.heading = row.heading;
          }
          return mapped;
        })
        .filter((row) => row.tabs.length > 0)
    : [{ tabs }];

  function renderTabButton(tab: MetaTab): ReactElement {
    const selected = activeTab?.id === tab.id;
    return (
      <button
        key={tab.id}
        type="button"
        role="tab"
        id={`${baseId}-tab-${tab.id}`}
        aria-selected={selected}
        aria-controls={`${baseId}-panel-${tab.id}`}
        onClick={() => onTabChange(tab.id)}
        className={`inline-flex items-center gap-1.5 px-5 py-3 ${
          selected
            ? 'border-b-2 border-wpaccent font-semibold text-wptext-strong'
            : 'text-wpaccent hover:text-wpaccent-hover'
        }`}
      >
        {tab.label}
        <CountBadge count={tab.badge ?? 0} label="未対応" />
      </button>
    );
  }

  return (
    <section className="rounded-sm border border-wpborder bg-white">
      {rows.map((row, rowIndex) => (
        <div
          key={row.heading ?? rowIndex}
          role="tablist"
          aria-label={row.heading ?? '関連情報'}
          className={`flex flex-wrap items-stretch border-b border-wpborder${
            rowIndex > 0 ? ' bg-[#f6f7f7]' : ''
          }`}
        >
          {row.heading && (
            <span className="flex shrink-0 items-center border-r border-wpborder px-4 py-3 text-xs font-semibold text-wptext-muted">
              {row.heading}
            </span>
          )}
          {row.tabs.map((tab) => renderTabButton(tab))}
        </div>
      ))}
      {activeTab && (
        <div
          role="tabpanel"
          id={`${baseId}-panel-${activeTab.id}`}
          aria-labelledby={`${baseId}-tab-${activeTab.id}`}
          className="px-5 py-5"
        >
          {activeTab.content}
        </div>
      )}
    </section>
  );
}

/** 詳細画面の定義リスト（ラベル: 値）。 */
export function FieldList({
  fields,
}: {
  fields: { label: string; value: ReactNode }[];
}): ReactElement {
  return (
    <dl className="grid grid-cols-1 gap-y-2.5 sm:grid-cols-[10rem_1fr]">
      {fields.map((field) => (
        <div key={field.label} className="contents">
          <dt className="text-wptext-muted">{field.label}</dt>
          <dd className="text-wptext-strong">{field.value}</dd>
        </div>
      ))}
    </dl>
  );
}
